import { isAxiosError } from 'axios';
import type { MetaPagedResult } from '~/apis/metaApi';
import type {
  CN03Draft,
  CultivarSuitability,
  SoilSuitability,
  YesNo,
} from '~/pages/cn03/form/types';
import { completionFromCreatedAt, defaultDraft } from '~/pages/cn03/form/types';

export type Cn03TriRef = { triId: string; name: string };

export type Cn03FormStatus = 'drafted' | 'approved' | 'rejected';
export type Cn03PermitType = 'new' | 'renewal';

export type Cn03FormListItem = {
  id: string;
  refId: string;
  planterName: string;
  permitType: Cn03PermitType;
  teaNurseryName: string;
  status: Cn03FormStatus;
  submittedDate: string;
  nextInspectionAt: string | null;
};

export type Cn03FormTriDetail = {
  triId: string;
  isAvailable: boolean;
  name?: string;
};

export type Cn03FormDetail = {
  id: string;
  refId: string;
  planterId: string;
  cn01Id: string;
  nic?: string | null;
  planterName: string;
  isNewPermit: boolean;
  permitNo: string | null;
  teaNurseryName: string;
  totalNo: number;
  certifiedNo: string;
  noOfSales: number;
  remainingCount: number;
  elevationMeters?: number | null;
  hasSufficientWater?: boolean | null;
  isLocationSuitable?: boolean | null;
  isSoilFumigation?: boolean | null;
  soilPhTested?: boolean | null;
  soilPhValue?: number | null;
  nurserySoilSuitability?: string | null;
  cultivarsRecommended?: boolean | null;
  hasAdequateTraining?: boolean | null;
  isRecommended?: boolean | null;
  status: Cn03FormStatus;
  landAk?: number | null;
  landRudi?: number | null;
  landP?: number | null;
  landHeck?: number | null;
  noPlants?: number | null;
  rejectionReasonId?: string | null;
  otherMatters?: string | null;
  personPresent?: string | null;
  investigatingOfficerName?: string | null;
  inspectionDate: string;
  tris: Cn03FormTriDetail[];
  districtId?: string | null;
  tiRange?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
};

export type Cn03ListFilters = {
  applicantName?: string;
  submittedDate?: string;
  status?: Cn03FormStatus;
  nextInspectionYear?: number;
  nextInspectionMonth?: number;
};

export type Cn03ListParams = Cn03ListFilters & {
  page: number;
  pageSize: number;
};

export type UpdateCn03FormResponse = {
  cn03FormId: string;
};

/** GET /cn03/permits */
export type Cn03PermitsResponse = {
  refId?: string;
  planterId: string;
  nic: string;
  planterName?: string | null;
  permits: string[];
  cn01Id?: string;
  isRenewal?: boolean;
  permitNo?: string | null;
  teaNurseryName?: string | null;
  cn01Tris?: Cn03TriRef[];
  districtId?: string;
};

/** GET /cn03/context */
export type Cn03ContextResult = {
  refId?: string;
  planterId: string;
  cn01Id: string;
  planterName?: string | null;
  districtId?: string;
  isRenewal: boolean;
  permitNo: string | null;
  teaNurseryName: string | null;
  cn01Tris: Cn03TriRef[];
};

/** GET /meta/cn03-reasons */
export type MetaCn03Reason = {
  id: string;
  reasonName: string | null;
};

export type Cn03SelectOption = {
  value: string;
  label: string;
};

/** POST /cn03/forms body (CreateCn03FormDto) */
export type CreateCn03FormBody = {
  planterId: string;
  cn01Id: string;
  planterName: string;
  isNewPermit: boolean;
  permitNo?: string;
  teaNurseryName?: string | null;
  totalNo: number;
  certifiedNo: string;
  noOfSales: number;
  remainingCount: number;
  elevationMeters?: number;
  hasSufficientWater?: boolean;
  isLocationSuitable?: boolean;
  isSoilFumigation?: boolean;
  soilPhTested?: boolean;
  soilPhValue?: number;
  nurserySoilSuitability?: string;
  cultivarsRecommended?: boolean;
  hasAdequateTraining?: boolean;
  isRecommended?: boolean | null;
  landAk?: number;
  landRudi?: number;
  landP?: number;
  landHeck?: number;
  noPlants?: number;
  rejectionReasonId?: string;
  otherMatters?: string;
  personPresent?: string;
  investigatingOfficerName?: string;
  inspectionDate: string;
  tiRange: string;
  tris: { triId: string; isAvailable: boolean }[];
};

export type CreateCn03FormResponse = {
  cn03FormId: string;
};

export function metaCn03ReasonsToOptions(reasons: MetaCn03Reason[]): Cn03SelectOption[] {
  return (reasons ?? []).map((r) => ({
    value: r.id,
    label: r.reasonName?.trim() ? r.reasonName : '(No label)',
  }));
}

export function buildCn03ListParams(
  filters: Cn03ListFilters,
  page: number,
  pageSize: number
): Cn03ListParams {
  const params: Cn03ListParams = { page, pageSize };
  const name = filters.applicantName?.trim();
  if (name) params.applicantName = name;
  const date = filters.submittedDate?.trim();
  if (date) params.submittedDate = date;
  if (filters.status) params.status = filters.status;
  if (filters.nextInspectionYear != null && filters.nextInspectionMonth != null) {
    params.nextInspectionYear = filters.nextInspectionYear;
    params.nextInspectionMonth = filters.nextInspectionMonth;
  }
  return params;
}

function optionalBoolToYesNo(v: boolean | null | undefined): YesNo {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function apiSoilToDraft(v: string | null | undefined): SoilSuitability {
  const t = String(v ?? '').toUpperCase();
  if (t === 'H') return 'highly';
  if (t === 'M') return 'moderately';
  if (t === 'N') return 'not';
  return '';
}

function intToString(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '';
  return String(v);
}

function numToString(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '';
  return String(v);
}

/** Combines officer name and designation for API `investigatingOfficerName`. */
export function combineInvestigatingOfficer(name: string, role: string): string {
  const n = name.trim();
  const r = role.trim();
  if (n && r) return `${n} - ${r}`.slice(0, 255);
  return (n || r).slice(0, 255);
}

/** Splits API `investigatingOfficerName` into name and designation when possible. */
export function splitInvestigatingOfficer(combined: string): { name: string; role: string } {
  const trimmed = combined.trim();
  if (!trimmed) return { name: '', role: '' };
  const dashIdx = trimmed.indexOf(' - ');
  if (dashIdx > 0) {
    return {
      name: trimmed.slice(0, dashIdx).trim(),
      role: trimmed.slice(dashIdx + 3).trim(),
    };
  }
  return { name: trimmed, role: '' };
}

/** Maps GET /cn03/forms/:id response to UI draft state. */
export function cn03DetailToDraft(detail: Cn03FormDetail): CN03Draft {
  const base = defaultDraft();
  const { completionDate, completionTime } = completionFromCreatedAt(detail.createdAt);
  const recommended =
    detail.isRecommended === true ? 'yes' : detail.isRecommended === false ? 'no' : '';
  const investigatingOfficer = splitInvestigatingOfficer(detail.investigatingOfficerName ?? '');

  return {
    ...base,
    refId: detail.refId,
    completionDate,
    completionTime,
    planterId: detail.planterId,
    cn01Id: detail.cn01Id,
    applicantNic: detail.nic?.trim() ?? '',
    applicantName: detail.planterName ?? '',
    inspectionType: detail.isNewPermit ? 'new' : 'extension',
    permitNo: detail.permitNo != null ? String(detail.permitNo) : '',
    permitSelected: !detail.isNewPermit && detail.permitNo != null,
    previousSeasonPlants: {
      totalNumber: intToString(detail.totalNo),
      certifiedNumber: detail.certifiedNo ?? '',
      numberOfSales: intToString(detail.noOfSales),
      remainingCount: intToString(detail.remainingCount),
    },
    teaNurseryName: detail.teaNurseryName ?? '',
    districtId: detail.districtId?.trim() ?? '',
    elevation: numToString(detail.elevationMeters),
    q06WaterSource: optionalBoolToYesNo(detail.hasSufficientWater),
    q07LocationSuitable: optionalBoolToYesNo(detail.isLocationSuitable),
    q08SoilFumigation: optionalBoolToYesNo(detail.isSoilFumigation),
    q09SoilPhTesting: optionalBoolToYesNo(detail.soilPhTested),
    soilPh: numToString(detail.soilPhValue),
    q11SoilSuitability: apiSoilToDraft(detail.nurserySoilSuitability),
    q12CultivarsRecommended: optionalBoolToYesNo(detail.cultivarsRecommended),
    cultivarRows: (detail.tris ?? []).map((t) => ({
      cultivarId: t.triId,
      cultivarName: t.name ?? '',
      suitability: (t.isAvailable ? 'suitable' : 'not_suitable') as CultivarSuitability,
    })),
    q14AdequateTraining: optionalBoolToYesNo(detail.hasAdequateTraining),
    recommendedEstablish: recommended,
    landSizeDetails: {
      acre: intToString(detail.landAk),
      rood: intToString(detail.landRudi),
      perch: intToString(detail.landP),
      hectare: intToString(detail.landHeck),
      numberOfPlants: intToString(detail.noPlants),
    },
    notRecommendedReasonId: detail.rejectionReasonId ?? '',
    otherMatters: detail.otherMatters ?? '',
    inspectorName: detail.personPresent ?? '',
    inspectionDate: detail.inspectionDate?.slice(0, 10) ?? base.inspectionDate,
    investigatingOfficerName: investigatingOfficer.name,
    investigatingOfficerRole: investigatingOfficer.role,
    tiRange: detail.tiRange?.trim() ?? '',
  };
}

export type { MetaPagedResult };

function yesNoToOptionalBool(v: YesNo): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function soilToApiLetter(v: SoilSuitability): string | undefined {
  if (v === 'highly') return 'H';
  if (v === 'moderately') return 'M';
  if (v === 'not') return 'N';
  return undefined;
}

function parseNonNegativeInt(raw: string): number {
  const n = Number.parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function parseOptionalFiniteNumber(raw: string): number | undefined {
  const t = String(raw).trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

function parsePermitNoString(draft: CN03Draft): string | undefined {
  const trimmed = draft.permitNo.trim();
  return trimmed || undefined;
}

export type DraftPayloadOptions = {
  /** When true, omit isRecommended so the form stays drafted. */
  asDraft?: boolean;
  /** Used for personPresent when the inspector name field is left blank. */
  fallbackPersonName?: string;
};

/**
 * Maps UI draft to backend CreateCn03FormDto. Call only after client-side validation.
 */
export function draftToCreateCn03Payload(
  draft: CN03Draft,
  options?: DraftPayloadOptions
): CreateCn03FormBody {
  const isNewPermit = draft.inspectionType === 'new';
  const permitNo = parsePermitNoString(draft);

  const ps = draft.previousSeasonPlants;
  const land = draft.landSizeDetails;

  const body: CreateCn03FormBody = {
    planterId: draft.planterId,
    cn01Id: draft.cn01Id,
    planterName: draft.applicantName.trim().slice(0, 255),
    isNewPermit,
    teaNurseryName: draft.teaNurseryName.trim() ? draft.teaNurseryName.trim().slice(0, 255) : null,
    totalNo: parseNonNegativeInt(ps.totalNumber),
    certifiedNo: (ps.certifiedNumber ?? '').trim().slice(0, 255),
    noOfSales: parseNonNegativeInt(ps.numberOfSales),
    remainingCount: parseNonNegativeInt(ps.remainingCount),
    inspectionDate: draft.inspectionDate.trim(),
    tiRange: draft.tiRange.trim(),
    tris: draft.cultivarRows.map((row) => ({
      triId: row.cultivarId,
      isAvailable: row.suitability === 'suitable',
    })),
  };

  if (!isNewPermit) {
    if (!permitNo) {
      throw new Error('Permit number is required for renewal of permit.');
    }
    body.permitNo = permitNo;
  }

  const elev = parseOptionalFiniteNumber(draft.elevation);
  if (elev !== undefined) body.elevationMeters = elev;

  const w = yesNoToOptionalBool(draft.q06WaterSource);
  if (w !== undefined) body.hasSufficientWater = w;
  const loc = yesNoToOptionalBool(draft.q07LocationSuitable);
  if (loc !== undefined) body.isLocationSuitable = loc;
  const fum = yesNoToOptionalBool(draft.q08SoilFumigation);
  if (fum !== undefined) body.isSoilFumigation = fum;
  const phTest = yesNoToOptionalBool(draft.q09SoilPhTesting);
  if (phTest !== undefined) body.soilPhTested = phTest;

  const phVal = parseOptionalFiniteNumber(draft.soilPh);
  if (phVal !== undefined) body.soilPhValue = phVal;

  const soilLetter = soilToApiLetter(draft.q11SoilSuitability);
  if (soilLetter) body.nurserySoilSuitability = soilLetter;

  const cul = yesNoToOptionalBool(draft.q12CultivarsRecommended);
  if (cul !== undefined) body.cultivarsRecommended = cul;
  const train = yesNoToOptionalBool(draft.q14AdequateTraining);
  if (train !== undefined) body.hasAdequateTraining = train;
  if (options?.asDraft) {
    body.isRecommended = null;
  } else {
    const rec = yesNoToOptionalBool(draft.recommendedEstablish);
    if (rec !== undefined) body.isRecommended = rec;
  }

  const hasLandData =
    options?.asDraft ||
    draft.recommendedEstablish === 'yes' ||
    [land.acre, land.rood, land.perch, land.hectare, land.numberOfPlants].some(
      (v) => v.trim() !== ''
    );

  if (hasLandData) {
    body.landAk = parseNonNegativeInt(land.acre);
    body.landRudi = parseNonNegativeInt(land.rood);
    body.landP = parseNonNegativeInt(land.perch);
    body.landHeck = parseNonNegativeInt(land.hectare);
    body.noPlants = parseNonNegativeInt(land.numberOfPlants);
  }

  const reason = draft.notRecommendedReasonId.trim();
  if (reason) body.rejectionReasonId = reason;

  const other = draft.otherMatters.trim().slice(0, 255);
  if (other) body.otherMatters = other;

  const personPresent = (
    draft.inspectorName.trim() || (options?.fallbackPersonName ?? '').trim()
  ).slice(0, 255);
  if (personPresent) body.personPresent = personPresent;

  const officer = combineInvestigatingOfficer(
    draft.investigatingOfficerName,
    draft.investigatingOfficerRole
  );
  if (officer) body.investigatingOfficerName = officer;

  return body;
}

export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }
  const data = error.response?.data as Record<string, unknown> | string | undefined;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const msg = data.message;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (Array.isArray(msg) && msg.length && typeof msg[0] === 'string') return msg.join(' ');
    if (typeof msg === 'object' && msg !== null && 'message' in msg) {
      const inner = (msg as { message?: string }).message;
      if (typeof inner === 'string' && inner.trim()) return inner;
    }
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
  }
  if (error.message?.trim()) return error.message;
  return fallback;
}
